import { useStore } from '@tanstack/react-store'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getFieldError, type TareaForm as TareaFormType } from '@/components/tareas/forms'
import {
  DIAS_SEMANA,
  FRECUENCIAS,
  admiteCorrerAHabil,
  describirRecurrencia,
  opcionesDiaModo,
  type DiaModo,
  type Frecuencia,
} from '@/components/tareas/forms/recurrenciaSchema'
import { Repeat } from 'lucide-react'

interface Props {
  form: TareaFormType
  /** Edita la regla de una repetición guardada: sin el checkbox "Repetir esta tarea". */
  edicion?: boolean
}

/**
 * Sección "Repetir" del formulario de tarea (ORR-304).
 *
 * Va plegada: quien carga una tarea suelta no ve más que un checkbox. Al
 * abrirla, la última línea traduce la regla a castellano — sin eso, "último día
 * hábil, cada 3 meses" se guarda sin que nadie sepa cuándo va a caer.
 */
export function RecurrenciaSection({ form, edicion = false }: Props) {
  const repetir = useStore(form.store, (s) => s.values.repetir)
  const frecuencia = useStore(form.store, (s) => s.values.rec_frecuencia) as Frecuencia
  const diaModo = useStore(form.store, (s) => s.values.rec_dia_modo) as DiaModo
  const diaDelMes = useStore(form.store, (s) => s.values.rec_dia_del_mes)
  const diaSemana = useStore(form.store, (s) => s.values.rec_dia_semana)
  const correrAHabil = useStore(form.store, (s) => s.values.rec_correr_a_habil)

  const esSemanal = frecuencia === 'semanal'

  return (
    <div className="space-y-3 rounded-lg border p-4">
      {edicion ? (
        <div className="space-y-1">
          <p className="flex items-center gap-2 text-sm font-medium">
            <Repeat className="h-4 w-4 text-muted-foreground" />
            Se repite
          </p>
          <p className="text-xs text-muted-foreground">
            Los cambios valen desde la próxima tarea. Las tareas ya creadas no cambian.
          </p>
        </div>
      ) : (
        <form.Field name="repetir">
          {(field) => (
            <label className="flex cursor-pointer items-center gap-2">
              <Checkbox
                checked={!!field.state.value}
                onCheckedChange={(checked) => field.handleChange(checked === true)}
              />
              <Repeat className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Repetir esta tarea</span>
            </label>
          )}
        </form.Field>
      )}

      {(edicion || repetir) && (
        <div className="space-y-3 pt-1">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <form.Field name="rec_frecuencia">
              {(field) => (
                <div className="space-y-2">
                  <Label>Cada cuánto</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value as Frecuencia)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FRECUENCIAS.map((f) => (
                        <SelectItem key={f.value} value={f.value}>
                          {f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            {esSemanal ? (
              <form.Field name="rec_dia_semana">
                {(field) => (
                  <div className="space-y-2">
                    <Label>Qué día</Label>
                    <Select
                      value={field.state.value != null ? String(field.state.value) : ''}
                      onValueChange={(value) => field.handleChange(Number(value))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Elegí el día" />
                      </SelectTrigger>
                      <SelectContent>
                        {DIAS_SEMANA.map((d) => (
                          <SelectItem key={d.value} value={String(d.value)}>
                            {d.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-sm text-destructive">
                        {getFieldError(field.state.meta.errors)}
                      </p>
                    )}
                  </div>
                )}
              </form.Field>
            ) : (
              <form.Field name="rec_dia_modo">
                {(field) => (
                  <div className="space-y-2">
                    <Label>Qué día</Label>
                    <Select
                      value={field.state.value}
                      onValueChange={(value) => field.handleChange(value as DiaModo)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {opcionesDiaModo(field.state.value).map((m) => (
                          <SelectItem key={m.value} value={m.value}>
                            {m.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </form.Field>
            )}
          </div>

          {!esSemanal && diaModo === 'dia_fijo' && (
            <form.Field name="rec_dia_del_mes">
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor={field.name}>Día del mes</Label>
                  <Input
                    id={field.name}
                    type="number"
                    min={1}
                    max={31}
                    className="w-24"
                    value={field.state.value ?? ''}
                    onChange={(e) =>
                      field.handleChange(e.target.value ? Number(e.target.value) : null)
                    }
                    onBlur={field.handleBlur}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {getFieldError(field.state.meta.errors)}
                    </p>
                  )}
                </div>
              )}
            </form.Field>
          )}

          {(esSemanal || admiteCorrerAHabil(diaModo)) && (
            <form.Field name="rec_correr_a_habil">
              {(field) => (
                <label className="flex cursor-pointer items-center gap-2">
                  <Checkbox
                    checked={!!field.state.value}
                    onCheckedChange={(checked) => field.handleChange(checked === true)}
                  />
                  <span className="text-sm">
                    Si cae feriado o fin de semana, pasarla al día hábil siguiente
                  </span>
                </label>
              )}
            </form.Field>
          )}

          <p className="rounded bg-muted px-3 py-2 text-sm text-muted-foreground">
            {describirRecurrencia({
              frecuencia,
              dia_modo: diaModo,
              dia_del_mes: diaDelMes,
              dia_semana: diaSemana,
              correr_a_habil: correrAHabil,
            })}
          </p>
        </div>
      )}
    </div>
  )
}
